import React, { useEffect, useMemo, useState, useRef } from "react";
import { clone, isEmpty, isNil } from "ramda";
import {
  asociarNumeroConLista,
  contarHastaIndice,
  createColor,
  getMeasure,
  shouldAddPoints,
  shouldAddPointsTotal,
} from "./utils";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { createRoot } from "react-dom/client";
import Legend from "../Gradient";
import "leaflet.offline";
import { IS_ONLINE, lista, MAP_TYPES } from "../../../../../config/configmap";
import { BOOLEAN_MEASURES } from "../../../../utils/consts";
import { useSelector } from "react-redux";
const MapAnalysis = ({
  measureName,
  assetData,
  spatialDataList,
  temporalDataList,
  intl,
  points,
  startIndex,
  endIndex,
  scales,
  percentiles,
  differences,
  equipmentData,
  dateTimeList,
  configData,
  assetModule,
}) => {
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const mapRef = useRef(null);
  const equipmentTypes = useSelector((state) => state.equipmentTypeData.data);
  const [objectMeasures, setObjectMeasures] = useState(null);
  const differencesForTempLength = useState([
    { range: 5000, multiplier: 1 },
    { range: 15000, multiplier: 2 },
    { range: 25000, multiplier: 3 },
  ]);

  useEffect(() => {
    if (!isNil(equipmentTypes)) {
      const newData = equipmentTypes.map((item) => {
        const measures = [];
        item.config?.measurands.forEach((measure, index) => {
          if (measure.origin === "measure") {
            measures.push(measure.ref);
          }
        });

        const newEntry = { name: item.name, measures: measures };
        return newEntry;
      });
      setObjectMeasures(newData);
      setObjectMeasures(newData);
    }
  }, [equipmentTypes]);

  const { min, max } = useMemo(() => {
    return createColor({ assetData, measureName, percentiles, scales, equipmentData, intl });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tColorScale = scales[measureName];
  useEffect(() => {
    if (!isNil(assetData)) {
      const mapContainer = document.getElementById(`mapAnalysis_${measureName}`);
      if (mapContainer && isNil(map)) {
        const newMap = L.map(mapContainer, { center: assetData.centroid, zoom: assetData.zoom });
        const tileLayer = IS_ONLINE ? "https://tile.openstreetmap.org/{z}/{x}/{y}.png" : MAP_TYPES[0].tileLayer;
        L.tileLayer(tileLayer, {
          maxZoom: 18,
          attribution: "OpenStreetMap",
        }).addTo(newMap);
        setMap(newMap);
      } else {
        console.error(`No se encontró el contenedor del mapa con ID 'mapAnalysis_${measureName}'.`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData]);

  const polylines = useMemo(() => {
    setLoading(true);
    if (!isNil(assetData) && !isNil(objectMeasures) && !isNil(differences) && !isNil(dateTimeList)) {
      const filteredPoints = [];
      for (let indexData = 0; indexData < dateTimeList.length; indexData++) {
        filteredPoints.push([]);
        const actualPoints = points[`${assetData.name}${dateTimeList[indexData][2] + 1}`];
        let temporalIndexes = [];
        if (!isEmpty(temporalDataList)) {
          temporalIndexes = temporalDataList.filter((x) => x.channel === dateTimeList[indexData][1]).map((x) => x.pk);
        }
        let prevValues = {
          state: null,
          values: spatialDataList?.[indexData]?.values?.[0], //primer valor de la magnitud,
          withColor: false,
        };
        let visualIndexes = clone(actualPoints.must_graph_points);
        if (!isEmpty(temporalIndexes) && !isNil(startIndex) && !isNil(endIndex)) {
          visualIndexes.push(startIndex);
          visualIndexes.push(endIndex);
          visualIndexes = visualIndexes.filter((item) => item >= startIndex);
          visualIndexes = visualIndexes.filter((item) => item <= endIndex);
        }
        if (!isEmpty(actualPoints) && !isNil(actualPoints.points)) {
          let mult = 4;
          for (let indexDiff = 0; indexDiff < differencesForTempLength[0].length; indexDiff++) {
            if (actualPoints.points.length < differencesForTempLength[0][indexDiff].range) {
              mult = differencesForTempLength[0][indexDiff].multiplier;
            }
          }
          for (let i = 0; i < actualPoints.points.length; i++) {
            let p = clone(actualPoints.points[i]);
            if (BOOLEAN_MEASURES.includes(measureName)) {
              p["measuresValue"] = 0;
              for (
                let additionalValuesIndex = 0;
                additionalValuesIndex < spatialDataList?.[indexData]?.values?.length;
                additionalValuesIndex++
              ) {
                if (
                  spatialDataList?.[indexData]?.values?.[additionalValuesIndex][0] <=
                  i <=
                  spatialDataList?.[indexData]?.values?.[additionalValuesIndex][1]
                ) {
                  p["measuresValue"] = 1;
                }
              }
            } else {
              p["measuresValue"] = spatialDataList?.[indexData]?.values?.[i];
            }

            let shouldAdd = false;
            const differenceMultiplier = measureName === "temperature" ? 1 * mult : 6;
            if (!isEmpty(temporalIndexes)) {
              const poss = temporalIndexes.findIndex((t) => t === p.pk);
              if (poss === 0) {
                prevValues.values = p["measuresValue"];
                prevValues.withColor = true;
              }
              shouldAdd = shouldAddPoints({
                indexes: actualPoints.must_graph_points,
                index: i,
                pk: p.pk,
                difference: BOOLEAN_MEASURES.includes(measureName)
                  ? 1
                  : differences[measureName] * differenceMultiplier,
                value: measureName === "strain" ? Math.abs(p["measuresValue"]) : p["measuresValue"],
                temporalIndexes,
                prevValues,
                filteredPoints: filteredPoints[indexData],
                visualIndexes,
                startIndex,
                endIndex,
              });
            } else {
              shouldAdd = shouldAddPointsTotal({
                indexes: actualPoints.must_graph_points,
                index: i,
                pk: p.pk,
                difference: BOOLEAN_MEASURES.includes(measureName)
                  ? 1
                  : differences[measureName] * differenceMultiplier,
                value: p["measuresValue"],
                temporalIndexes,
                prevValues,
                filteredPoints: filteredPoints[indexData],
                visualIndexes,
              });
            }
            if (shouldAdd) {
              filteredPoints[indexData].push({
                ...p,
                index: i,
                withColor: prevValues.withColor,
              });
            }
          }
        }
      }

      const pList = [];
      for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
        pList.push([]);
        for (let i = 0; i < filteredPoints[indexDTList].length - 1; i++) {
          const p1 = clone(filteredPoints[indexDTList][i]);
          const p2 = clone(filteredPoints[indexDTList][i + 1]);
          pList[indexDTList].push({ p1, p2, withColor: p1.withColor });
        }
      }

      setLoading(false);
      return pList;
    } else if (isNil(dateTimeList) && !isNil(assetData) && !isNil(objectMeasures)) {
      const filteredPoints = [[]];
      const actualPoints = points;

      let prevValues = {
        state: null,
        values: 0.5, //primer valor de la magnitud,
        withColor: false,
      };
      let temporalIndexes = [];
      if (!isEmpty(temporalDataList)) {
        temporalIndexes = temporalDataList.filter((x) => x.pk);
      }
      let visualIndexes = clone(actualPoints.must_graph_points);
      if (!isEmpty(temporalIndexes) && !isNil(startIndex) && !isNil(endIndex)) {
        visualIndexes.push(startIndex);
        visualIndexes.push(endIndex);
        visualIndexes = visualIndexes.filter((item) => item >= startIndex);
        visualIndexes = visualIndexes.filter((item) => item <= endIndex);
      }

      if (!isEmpty(actualPoints) && !isNil(actualPoints.points)) {
        let mult = 4;
        for (let indexDiff = 0; indexDiff < differencesForTempLength[0].length; indexDiff++) {
          if (actualPoints.points.length < differencesForTempLength[0][indexDiff].range) {
            mult = differencesForTempLength[0][indexDiff].multiplier;
          }
        }
        for (let i = 0; i < actualPoints.points.length; i++) {
          let p = clone(actualPoints.points[i]);
          if (BOOLEAN_MEASURES.includes(measureName)) {
            p["measuresValue"] = 0;
            for (
              let additionalValuesIndex = 0;
              additionalValuesIndex < spatialDataList?.[0]?.values?.length;
              additionalValuesIndex++
            ) {
              if (
                spatialDataList?.[0]?.values?.[additionalValuesIndex][0] <=
                i <=
                spatialDataList?.[0]?.values?.[additionalValuesIndex][1]
              ) {
                p["measuresValue"] = 1;
              }
            }
          } else {
            p["measuresValue"] = spatialDataList?.[0]?.values?.[i];
          }

          let shouldAdd = false;
          const differenceMultiplier = measureName === "temperature" ? 1 * mult : 6;
          if (!isEmpty(temporalIndexes)) {
            const poss = temporalIndexes.findIndex((t) => t === p.pk);
            if (poss === 0) {
              prevValues.values = p["measuresValue"];
              prevValues.withColor = true;
            }
            shouldAdd = shouldAddPoints({
              indexes: actualPoints.must_graph_points,
              index: i,
              pk: p.pk,
              difference: BOOLEAN_MEASURES.includes(measureName) ? 1 : differences[measureName] * differenceMultiplier,
              value: measureName === "strain" ? Math.abs(p["measuresValue"]) : p["measuresValue"],
              temporalIndexes,
              prevValues,
              filteredPoints: filteredPoints[0],
              visualIndexes,
              startIndex,
              endIndex,
            });
          } else {
            shouldAdd = shouldAddPointsTotal({
              indexes: actualPoints.must_graph_points,
              index: i,
              pk: p.pk,
              difference: BOOLEAN_MEASURES.includes(measureName) ? 1 : differences[measureName] * differenceMultiplier,
              value: p["measuresValue"],
              temporalIndexes: [],
              prevValues,
              filteredPoints: filteredPoints[0],
              visualIndexes,
            });
          }

          if (shouldAdd) {
            filteredPoints[0].push({
              ...p,
              index: i,
              withColor: prevValues.withColor,
            });
          }
        }
        const pList = [];
        pList.push([]);
        for (let i = 0; i < filteredPoints[0].length - 1; i++) {
          const p1 = clone(filteredPoints[0][i]);
          const p2 = clone(filteredPoints[0][i + 1]);
          pList[0].push({ p1, p2, withColor: p1.withColor });
        }
        setLoading(false);
        return pList;
      }
    }
    setLoading(false);
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spatialDataList, temporalDataList]);

  const haveTemporalData = useMemo(() => {
    if (!isEmpty(polylines) && !isEmpty(temporalDataList)) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polylines]);

  useEffect(() => {
    if (!isNil(map) && !isNil(tColorScale)) {
      map.eachLayer((layer) => {
        if (layer instanceof L.Polyline) {
          map.removeLayer(layer);
        }
      });
      if (haveTemporalData) {
        if (!isNil(dateTimeList)) {
          for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
            for (let i = 0; i < polylines[indexDTList].length; i++) {
              //const { min, max } = assetMinMax;
              const weight = haveTemporalData && polylines[indexDTList][i].withColor ? 10 : 5;
              const color = polylines[indexDTList][i].withColor
                ? tColorScale(
                    getMeasure(polylines[indexDTList][i].p1.measuresValue, min, max, null, tColorScale.domain()) ?? 0,
                  )
                : "gray";
              var poly = L.polyline(
                [
                  [polylines[indexDTList][i].p1.latitude, polylines[indexDTList][i].p1.longitude],
                  [polylines[indexDTList][i].p2.latitude, polylines[indexDTList][i].p2.longitude],
                ],
                { color: color, weight: weight },
              );
              if (color !== "gray") {
                poly.on("click", function (e) {
                  const googleMapsURL = `https://www.google.com/maps/dir//${polylines[indexDTList][i].p1.latitude},${polylines[indexDTList][i].p1.longitude}`;
                  window.open(googleMapsURL, "_blank");
                });
              }
              poly.addTo(map);
            }
          }
        } else {
          for (let indexPoly = 0; indexPoly < polylines[0].length; indexPoly++) {
            const weight = haveTemporalData && polylines[0][indexPoly].withColor ? 20 : 5;

            const color = polylines[0][indexPoly].withColor
              ? tColorScale(getMeasure(polylines[0][indexPoly].p1.measuresValue, min, max, null, tColorScale.domain()))
              : "gray";
            const poly = L.polyline(
              [
                [polylines[0][indexPoly].p1.latitude, polylines[0][indexPoly].p1.longitude],
                [polylines[0][indexPoly].p2.latitude, polylines[0][indexPoly].p2.longitude],
              ],
              { color: color, weight: weight },
            );
            const measurand = polylines[0][indexPoly].p1?.measuresValue?.toFixed(2);
            const pk = polylines[0][indexPoly].p1.pk.toFixed(2);

            const popUpContent = `<h3>${
              equipmentData.channels[spatialDataList[0].channel - 1].name
            } - ${spatialDataList[0].datetime.replace("T", " ")}</h3><h4>PK: ${pk}</h4><h4>${intl.formatMessage({
              id: measureName,
            })}: ${measurand}</h4>`;
            poly.bindPopup(popUpContent);
            poly.addTo(map);
          }
        }
      } else {
        const diffChannels = [];
        if (!isNil(dateTimeList)) {
          const moduleData = configData.modules.find((module) => module.name === assetModule);
          const packageData = moduleData.packages.find((packg) => packg.name === measureName);
          for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
            diffChannels.push(equipmentData.channels[dateTimeList[indexDTList][1] - 1]);
          }
          for (let indexDTList = 0; indexDTList < dateTimeList.length; indexDTList++) {
            for (let i = 0; i < polylines[indexDTList].length; i++) {
              // falta la comparación para ver si hay otro con el mismo canal para poner offset

              const weight = haveTemporalData && polylines[indexDTList][i].withColor ? 20 : 5;
              const color = tColorScale(
                getMeasure(polylines[indexDTList][i].p1.measuresValue, min, max, null, tColorScale.domain()),
              );

              const realIndex = contarHastaIndice(diffChannels, indexDTList) - 1;
              const length = contarHastaIndice(
                diffChannels,
                diffChannels.lastIndexOf(equipmentData.channels[dateTimeList[indexDTList][1] - 1]),
              );
              const asociado = asociarNumeroConLista(14, lista);

              const m = (realIndex + 1) % 2 === 0 ? 1 : -1;
              const extra = length % 2 === 0 ? Math.round((realIndex + 1) / 2) : Math.round(realIndex / 2);
              const distance = asociado * m * extra;
              const pointIni = [polylines[indexDTList][0].p1.latitude, polylines[indexDTList][0].p1.longitude];
              const pointEnd = [
                polylines[indexDTList][polylines[indexDTList].length - 1].p2.latitude,
                polylines[indexDTList][polylines[indexDTList].length - 1].p2.longitude,
              ];
              const atanV1 = Math.atan2(pointEnd[0] - pointIni[0], pointEnd[1] - pointIni[1]);
              const offset_v1_latitude = length === 1 ? 0 : -distance * Math.cos(atanV1);
              const offset_v1_longitude = length === 1 ? 0 : distance * Math.sin(atanV1);
              // eslint-disable-next-line no-redeclare
              var poly = L.polyline(
                [
                  [
                    polylines[indexDTList][i].p1.latitude + offset_v1_latitude,
                    polylines[indexDTList][i].p1.longitude + offset_v1_longitude,
                  ],
                  [
                    polylines[indexDTList][i].p2.latitude + offset_v1_latitude,
                    polylines[indexDTList][i].p2.longitude + offset_v1_longitude,
                  ],
                ],
                { color: color, weight: weight },
              );
              const measurand = polylines[indexDTList][i].p1?.measuresValue?.toFixed(2);
              const pk = polylines[indexDTList][i].p1.pk.toFixed(2);

              var popUpContent = `<h3>${equipmentData.channels[dateTimeList[indexDTList][1] - 1].name} - ${dateTimeList[
                indexDTList
              ][0].replace("T", " ")}</h3><h4>PK: ${pk}</h4><h4>${intl.formatMessage({
                id: measureName,
              })}: ${measurand} ${packageData.unit}</h4>`;
              poly.bindPopup(popUpContent);
              poly.addTo(map);
            }
          }
        } else {
          //MOMENTANEO
          if (!isNil(polylines) && !isEmpty(polylines)) {
            for (let indexPoly = 0; indexPoly < polylines[0].length; indexPoly++) {
              const weight = haveTemporalData && polylines[0][indexPoly].withColor ? 20 : 5;
              const color = tColorScale(
                getMeasure(polylines[0][indexPoly].p1.measuresValue, min, max, null, tColorScale.domain()),
              );
              const poly = L.polyline(
                [
                  [polylines[0][indexPoly].p1.latitude, polylines[0][indexPoly].p1.longitude],
                  [polylines[0][indexPoly].p2.latitude, polylines[0][indexPoly].p2.longitude],
                ],
                { color: color, weight: weight },
              );
              const measurand = polylines[0][indexPoly].p1?.measuresValue?.toFixed(2);
              const pk = polylines[0][indexPoly].p1.pk.toFixed(2);

              const popUpContent = `<h3>${
                equipmentData.channels[spatialDataList[0].channel - 1].name
              } - ${spatialDataList[0].datetime.replace("T", " ")}</h3><h4>PK: ${pk}</h4><h4>${intl.formatMessage({
                id: measureName,
              })}: ${measurand}</h4>`;
              poly.bindPopup(popUpContent);
              poly.addTo(map);
            }
          }
        }

        const customComponent = document.getElementById(`gradient_${measureName}`);
        const root = createRoot(customComponent);
        root.render(
          <Legend
            assetData={assetData}
            equipmentData={equipmentData}
            measureName={measureName}
            percentiles={percentiles}
            scales={scales}
            intl={intl}
          />,
        );
      }
      map.setView(assetData.centroid, assetData.zoom);
      fitTheBounds();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polylines, map]);

  function fitTheBounds() {
    if (mapRef.current && !isNil(map)) {
      const currentBounds = map.getBounds();
      const southWest = currentBounds.getSouthWest(); // Devuelve un LatLng
      const northEast = currentBounds.getNorthEast(); // Devuelve un LatLng
      map.setMaxBounds([
        [southWest.lat - 0.12, southWest.lng - 0.12],
        [northEast.lat + 0.12, northEast.lng + 0.12],
      ]);
    }
  }

  if (isNil(polylines) || loading) {
    return (
      <div
        style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}
      >{`${intl.formatMessage({ id: "loading" })}...`}</div>
    );
  }

  return (
    <>
      <div id={`mapAnalysis_${measureName}`} style={{ width: "47vw", height: "40vh" }} ref={mapRef}>
        <div
          id={`gradient_${measureName}`}
          style={{
            position: "absolute",
            bottom: "10px",
            left: "10px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            padding: "10px",
            borderRadius: "5px",
            zIndex: 500,
            width: "13vh",
            height: "21vh",
          }}
          className="leaflet-bottom leaflet-leaflet"
        ></div>
      </div>
    </>
  );
};

export default MapAnalysis;
